import {
  isBaseline,
  isCore,
  isCorePaid,
  isWebWinback,
  isEnLanguage,
  isInApp,
  isInApp14DayiOS,
  isWinback,
  isEmailMain,
  isIOS,
  isUSWinback,
  isApp14DayBaseRoute,
  isCountry,
  isUS,
  isIntl,
  isDeLanguage,
  isCoreDirect,
  isIntlBaseline,
  isUSInAppWinback,
  isAffiliateWithoutTrial,
  isDeEmailMain,
  isEsEmailMain,
  isEsLanguage,
  isInAppWinback,
  isAdWordsSEM,
  isHM,
  isDE,
  isDPP,
  isUsCore,
} from ".";
import { routeConstants } from "../constants";
import getStore, { CoreReduxState } from "../redux/store";

import { isFromEmail, isUSEmail } from "./email";
import { checkSegments } from "./util";
import {
  ELIGIBLE_HEALTH_RISKS,
  EligibleStateCode,
  OPTIMIZELY_TARGETED_FOR_TELEHEALTH,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { getCourseDuration } from "@utils/calculateGoals";
import { getMeristemContext } from "@utils/meristemContext";
import { getTrackingParams } from "../services/VisitTracker";
import { canRetrieveWeight } from "../refactoredSurvey";
import { getSingleOffers } from "../../hooks/useOffersContext";
import { getSingleAddOns } from "../../hooks/useAddOnsContext";
import { getSingleInAppOffers } from "../../hooks/useInAppOffersContext";
import { TELEHEALTH_PLAN } from "src/funnels/payment-en/pages/telehealthRedirects";
import { appConfig } from "src/config";
import { loadWinbackInfo } from "../redux/slices/winbackInfo";
import { calculateBMIImperial } from "src/primitive/bmi";
import {
  allowsITunesPlanAssignment,
  isAppBaseRoute,
  isInAppNoTrial,
} from "./in-app";
import { isMealPlan, isMealWorkoutBundle, isWorkoutPlan } from "../addons";
import { isEmail } from "../validate";

const HW_NON_VIP_BATCH_ID_DEV = 1;
const HW_NON_VIP_BATCH_ID_PROD = 354;
export const IOS_OVERRIDE_PLAN = "1-4499";
export const IOS_OVERRIDE_PLAN_NO_TRIAL = "1-4499-notrial";
export const DPP_OVERRIDE_PLAN = "dpp-12-month";
export const USD_ANNUAL_PLAN_ID = "option_j_20171222";
export const USD_MONTHLY_PLAN_ID = "option_d_20210324_ex149_14d";

const devModeFlagSet =
  new URLSearchParams(window.location.search).get("idm") === "true";

export function isDevMode() {
  return devModeFlagSet;
}

export const isCollapsedPaymentEligible = (state?: CoreReduxState) => {
  return !isInApp(state);
};

export const isHMEligible = (state?: CoreReduxState) =>
  isUS(state) && isEnLanguage(state) && isCore(state) && isIOS();

const hadPreviousITunesSubscription = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // This parameter is set by the growth backend in the internal app endpoint
  // More details about that here: https://github.com/noom/eng-handbook/blob/master/teams/growth/product/Winbacks.md#more-about-in-app-routes
  return urlParams.get("had_itunes_sub") === "true";
};

export const isSurveyOnLPEligible = checkSegments({
  oneOf: [routeConstants.exsf01d, routeConstants.exsf01],
  noneOf: [isHM, isInApp],
});

export const isBraintreeStripeiOSEligible = checkSegments({
  allOf: [isUS, isEnLanguage, hadPreviousITunesSubscription],
  oneOf: [routeConstants.app14DayReturning],
});

/**
 * Checks if the user can use the native app in-app purchase flow.
 */
export const isInAppNativePurchaseEligible = checkSegments({
  allOf: [isEnLanguage],
  noneOf: [isBraintreeStripeiOSEligible],
  oneOf: [isInApp14DayiOS],
});

export const isITunesPlansEligible = checkSegments({
  allOf: [
    isInAppNativePurchaseEligible,
    isAppBaseRoute,
    allowsITunesPlanAssignment,
  ],
});

/**
 * Checks if the user can use the web-based in-app purchase flow.
 */
export const isInAppWebPurchaseEligible = checkSegments({
  oneOf: [
    routeConstants.app14DayFtWeb,
    routeConstants.app14DayWeb,
    routeConstants.app14DayWebMain,
    isBraintreeStripeiOSEligible,
  ],
});

export const isFastForwardEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan],
  oneOf: [isCore, routeConstants.winback],
});

export const isSupporterPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isAffiliateWithoutTrial,
    routeConstants.emailMain,
    routeConstants.referrals,
    routeConstants.freeTrial,
    isWebWinback,
  ],
});

const isNoomPremiumEligibleUS = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isBaseline,
    isWebWinback,
    routeConstants.affiliateTrialNPB,
    routeConstants.emailMain,
    routeConstants.referrals,
  ],
});

const isNoomPremiumEligibleIntl = checkSegments({
  allOf: [isIntl, isEnLanguage],
  oneOf: [
    isBaseline,
    isWebWinback,
    routeConstants.affiliateTrialNPB,
    routeConstants.emailMain,
    routeConstants.referrals,
    routeConstants.freeTrial,
  ],
});

export const isAddonStoreEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isInApp, isTelehealthPlan],
  oneOf: [
    routeConstants.direct,
    routeConstants.exsf01d,
    routeConstants.exsf01,
    isAffiliateWithoutTrial,
    routeConstants.emailMain,
    routeConstants.referrals,
    routeConstants.freeTrial,
    isWebWinback,
  ],
});

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [
    isBaseline,
    isDeEmailMain,
    routeConstants.winback,
    routeConstants.app14DayWeb,
    routeConstants.app14Day,
    routeConstants.app14DayFtWeb,
    routeConstants.app14DayReturning,
  ],
});

export const userHasPurchasedNoomPremium = () => {
  const { purchasedNoomPremium } = getStore().getState();
  return purchasedNoomPremium;
};

export const isNoomPremiumConsultationSurveyEligible = checkSegments({
  allOf: [isUS, userHasPurchasedNoomPremium],
  oneOf: [
    isBaseline,
    routeConstants.affiliateTrialNPB,
    routeConstants.referrals,
  ],
});

export const isImportantDateEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isBaseline, isWebWinback, isFromEmail, isApp14DayBaseRoute],
});

export const isFivePercentEligible = checkSegments({
  allOf: [isUS],
  oneOf: [isBaseline, isApp14DayBaseRoute],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [
    isBaseline,
    isWinback,
    isUSEmail,
    isDeEmailMain,
    isInApp,
    isUSInAppWinback,
  ],
});

export const isFinalGraphEligible = checkSegments({
  oneOf: [
    isBaseline,
    isUSWinback,
    isUSEmail,
    isApp14DayBaseRoute,
    isUSInAppWinback,
  ],
});

// TODO: Remove isDeLanguage once Noom Premium and Addons can successfully charge SEPA
// https://noomhq.atlassian.net/browse/GT-1256
export const isSEPAEligible = checkSegments({
  allOf: [isDE, isDeLanguage],
});

/**
 * Verifies if the current user should see the final update graph
 */
export const shouldShowFinalUpdateGraph = (
  { userData } = getStore().getState()
) => {
  const { weight, idealWeight, weightKg, idealWeightKg } =
    userData[userData?.surveyNameSpace] || {};
  const courseDuration = getCourseDuration();

  // EnUSBaseline + App14DayBase routes
  if (
    weight &&
    idealWeight &&
    weightKg &&
    idealWeightKg &&
    isFivePercentEligible() &&
    courseDuration <= 8
  ) {
    return true;
  }

  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

export const shouldCondenseGraphSteps = () => {
  const courseDuration = getCourseDuration();
  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

function shouldCondenseOrShowFinalUpdateGraph(courseDuration: number) {
  const isCourseDurationEligible =
    courseDuration >= 2 &&
    ((courseDuration <= 8 && isIntlBaseline()) || courseDuration <= 6);
  return (
    canRetrieveWeight() && isFinalGraphEligible() && isCourseDurationEligible
  );
}

export const hasNoTrial = checkSegments({
  oneOf: [
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
    routeConstants.appNoTrial,
    routeConstants.appNoTrialReturning,
    routeConstants.ndpp,
  ],
});

export const isDirectTraffic = checkSegments({
  oneOf: [
    routeConstants.direct,
    routeConstants.appUpsell,
    routeConstants.hmDirect,
  ],
});

export const isTrialFeeWaived = checkSegments({
  oneOf: [
    isInAppWinback,
    routeConstants.app14DayMain,
    routeConstants.app14Day,
    routeConstants.freeTrial,
    routeConstants.referrals,
    routeConstants.affiliateTrialNPB,
    routeConstants.affiliate,
    routeConstants.phillips,
    routeConstants.humana,
  ],
});

export const isChooseTrialPriceNewStyleEligible = checkSegments({
  oneOf: [isCore, isEmailMain],
});

export const isChooseTrialFeeHidden = (state?: CoreReduxState) =>
  isCountry(["KR", "JP"], state);

export const isTrialEligible = (
  { recommendedPlan } = getStore().getState()
) => {
  return recommendedPlan.has_trial && !hasNoTrial();
};

export function showChooseTrialPage(state = getStore().getState()) {
  if (!state.serverContext.trial_fees) {
    return false;
  }
  if (isEnLanguage(state)) {
    return (
      !(isTrialFeeWaived() || hasNoTrial()) && !isChooseTrialFeeHidden(state)
    );
  }
  return (
    isCoreDirect(state) ||
    isCorePaid(state) ||
    isDeEmailMain() ||
    isEsEmailMain() ||
    isHM()
  );
}

export const isPromoCodeBoxDisabled = checkSegments({
  allOf: [
    (state?: CoreReduxState) =>
      isEnLanguage(state) || isDeLanguage(state) || isEsLanguage(state),
  ],
  noneOf: [isAdWordsSEM],
  oneOf: [isInApp, isCorePaid, isWinback, routeConstants.affiliateNoTrial],
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [
    routeConstants.app14DayReturning,
    routeConstants.app14DayWeb,
    routeConstants.app14DayFtWeb,
    routeConstants.app14Day,
    routeConstants.appNoTrial,
  ],
});

export const isPaidTraffic = checkSegments({
  oneOf: [
    isCorePaid,
    routeConstants.experiment,
    routeConstants.affiliateTrialNPB,
    routeConstants.hmPaid,
    routeConstants.hmExsf01,
    routeConstants.hmExsf01d,
    routeConstants.hmAwsem,
    routeConstants.winback,
    routeConstants.hmWinback,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [isBaseline, isWinback, isApp14DayBaseRoute],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [
    isBaseline,
    isWinback,
    routeConstants.app14Day,
    routeConstants.app14DayWeb,
    routeConstants.hmPaid,
    routeConstants.hmDirect,
  ],
});

// used exclusively for users coming through the initial buyflow
// determines if the user will even see the insurance reimbursement survey
export const isEligibleForTelehealthReimbursement = (
  { surveyAnswers, geoLocation } = getStore().getState()
): boolean => {
  // this value is set (and can be turned on/off, throttled) by Optimizely
  // (2021/08/10) https://app.optimizely.com/v2/projects/2753280231/experiments/20510260333
  const optimizelyTargetedForTelehealth = sessionStorage.getItem(
    OPTIMIZELY_TARGETED_FOR_TELEHEALTH
  );

  const isEligibleState =
    geoLocation?.country_code === "US" &&
    geoLocation?.subdivision in EligibleStateCode;

  let currentBMI: number;
  const { heightFeet, heightInch, weight } = surveyAnswers;

  if (
    heightFeet !== undefined &&
    heightInch !== undefined &&
    weight !== undefined
  ) {
    currentBMI = calculateBMIImperial(heightFeet, heightInch, weight);
  }
  const risks = (surveyAnswers?.currentHealthRisk ?? []) as string[];
  const userHealthRisks: string[] = risks.concat(
    surveyAnswers?.currentHealthRiskMale ?? []
  );
  const isEligibleHealthRisk = userHealthRisks.some((risk) =>
    ELIGIBLE_HEALTH_RISKS.includes(risk)
  );

  return (
    optimizelyTargetedForTelehealth &&
    isEligibleState &&
    isCore() &&
    (currentBMI >= 30 || (currentBMI >= 25 && isEligibleHealthRisk))
  );
};

export function isTelehealthPlan(state = getStore().getState()) {
  return state.recommendedPlan.noom_plan_id === TELEHEALTH_PLAN;
}

/**
 * Signals if the current user/experience is subject to HIPPA data protection rules.
 * This can be used to filter logging/etc as appropriate to ensure compliance.
 */
export const isHipaa = (): boolean => {
  return (
    getMeristemContext()?.context_type === "telehealth" || isTelehealthPlan()
  );
};

export const isHmSuperswitchEligible = (
  state = getStore().getState()
): boolean => {
  const { activeSubscriptionData } = state;
  return (
    isEnLanguage(state) &&
    activeSubscriptionData.hasActiveSubscription &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug.endsWith("en_US") &&
    ((activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_PROD &&
      appConfig.NOOM_ENV === "production") ||
      activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_DEV)
  );
};

export const isEligibleForExitIntentModalEmail = () => {
  return isIntl() && isEmailMain() && getTrackingParams().utm_source === "drip";
};

export const hasPromoCode = () => {
  const { promoCode } = getStore().getState();

  // TODO shouldn't rely on URL params for this; and if we are, should be an arg to this function
  const code = new URLSearchParams(window.location.search).get("promoCode");
  return !!(code && code.length) || !!promoCode?.promoCodeApplied;
};

export const hasVipPromoCode = () => {
  const { promoCode } = getStore().getState();

  return hasPromoCode() && promoCode?.promoCodeIsVip;
};

export const hasOffers = (state: CoreReduxState) => {
  const { offers } = state;
  return offers.hasOffers;
};
export const isTaxExclusiveCountry = (state?: CoreReduxState) =>
  isCountry(["US", "CA"], state);

export const hasSingleOffers = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleOffers(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleAddOns = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleAddOns(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleInAppOffers = () => {
  const { serverContext, userData } = getStore().getState();
  const singleOffers = getSingleInAppOffers(
    serverContext,
    userData.NoomPremiumSurveyCompleteDateISO
  );

  return singleOffers.length > 0;
};

export const hasEnrollment = () => {
  const { serverContext } = getStore().getState();
  return serverContext?.enrollment;
};

export const isNoomPremiumConsultationSurveyDisqualified = (
  state = getStore().getState()
) => {
  const { surveyAnswers } = state;
  return surveyAnswers?.workoutPlanLimitations?.includes("painOrLosingBreath");
};

export const isNoomPremiumEligible = () => {
  const { promoCode } = getStore().getState();
  const isNoomPremiumEligibleBySegments =
    isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();

  return !promoCode?.promoCodeIsVip && isNoomPremiumEligibleBySegments;
};

export function isEligibleForCheckoutTimer(state = getStore().getState()) {
  return (
    isBaseline() && !isTelehealthPlan() && !state.recommendedPlan.isForcedPlan
  );
}

export const requiresDoubleOptInForEmail = (state?: CoreReduxState) => {
  return isCountry(["DE", "AT"], state);
};

export const showAppReviewBadges = () => {
  return !isInApp() && !isUS() && !isIntl();
};

export const isWinbackUserAndNotReup = () => {
  const { winbackInfo } = getStore().getState();

  return (
    ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus) &&
    !winbackInfo.willReup
  );
};
isWinbackUserAndNotReup.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isWinbackUserOrReUp = () => {
  const { winbackInfo } = getStore().getState();

  return (
    ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus) ||
    winbackInfo.willReup
  );
};

isWinbackUserOrReUp.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const showPromoBox = checkSegments({
  noneOf: [routeConstants.exsf01, routeConstants.exsf01d, isWinback],
});

export function determinePlanOverride() {
  if (isInAppNoTrial()) {
    return IOS_OVERRIDE_PLAN_NO_TRIAL;
  }
  if (isInApp14DayiOS()) {
    return IOS_OVERRIDE_PLAN;
  }
  if (isDPP()) {
    return DPP_OVERRIDE_PLAN;
  }
  return "";
}

export function shouldShowCoursePackOffers() {
  const { promoCode, recommendedPlan } = getStore().getState();
  return (
    isUsCore() &&
    isEnLanguage() &&
    !isTelehealthPlan() &&
    !promoCode.promoCodeIsVip &&
    recommendedPlan.has_trial &&
    !isWinbackUserOrReUp()
  );
}

shouldShowCoursePackOffers.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export function acceptedCoursePackOffer() {
  const { checkout, paymentAddon } = getStore().getState();
  return (
    checkout.toggledCoursePackOffer &&
    paymentAddon.hasOffer &&
    shouldShowCoursePackOffers()
  );
}

export function hasBoughtMealPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.some((p) => isMealPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.some((p) => isWorkoutPlan(p) || isMealWorkoutBundle(p));
}

export const isEligibleForHmCheckout = (
  { userData } = getStore().getState()
) => {
  const { email } = userData;
  const emailValid = email && isEmail(email);
  const allowInAppPurchase = isInAppWebPurchaseEligible();
  const isAllowedThrough = emailValid || isDevMode() || allowInAppPurchase;
  return isAllowedThrough;
};

/**
 * Verifies if current user is on referral routes and wants a full buyflow
 */
export const isFullPersonalizationBuyflow = checkSegments({
  noneOf: [
    ({ surveyAnswers }: CoreReduxState) => {
      return surveyAnswers.customizationChoice?.[0] === "quickStart";
    },
  ],
});

export const showMonthlyPlanChoice = () => {
  const showMonthlyChoiceURLParam = new URLSearchParams(
    window.location.search
  ).get("show_monthly_choice");
  return showMonthlyChoiceURLParam && isEmailMain() && isUS();
};

export function isProgramFit() {
  const { surveyAnswers } = getStore().getState();
  const {
    emotionalEating,
    shortTermSuccess,
    multitaskingEating,
    eatingServing,
  } = surveyAnswers;

  return (
    (emotionalEating?.[0] +
      shortTermSuccess?.[0] +
      multitaskingEating?.[0] +
      eatingServing?.[0] || 0) >= 8
  );
}
